/**
 * GlobalRevisionsSection - Main section that appears above store cards
 * Auto-shows when there are recent global revisions
 */

import { useState, useEffect } from 'react';
import './GlobalRevisionsCarousel.css';
import {
    Calendar,
    ChevronDown,
    ChevronRight,
    Eye,
    RefreshCw,
    AlertTriangle,
    Info,
    X,
    ChevronLeft
} from 'lucide-react';
import GlobalRevisionCard from './GlobalRevisionCard.jsx';
import GlobalRevisionsModal from './GlobalRevisionsModal.jsx';
import { useGlobalRevisions } from './hooks/useGlobalRevisions.js';

export default function GlobalRevisionsSection() {
    const {
        // Recent revisions for display
        recentRevisions,
        selectedWeek,
        recentStats,
        loadingRecent,
        recentError,
        hasRecentRevisions,
        shouldShowSection,

        // Modal functionality
        isModalOpen,
        allRevisions,
        allStats,
        loadingAll,
        allError,
        hasMoreRevisions,
        filters,

        // Actions
        refreshRecent,
        openModal,
        closeModal,
        loadMoreRevisions,
        searchRevisions,
        updateFilters,

        // Meta
        testMode
    } = useGlobalRevisions({
        autoFetch: true,
        testMode: false,
        initialLimit: 5
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Safety fallback for hasRecentRevisions
    const safeHasRecentRevisions = hasRecentRevisions ?? (recentRevisions && recentRevisions.length > 0);

    // For debugging - always show the component for now
    console.log('GlobalRevisionsSection debug:', {
        isDismissed,
        shouldShowSection,
        hasRecentRevisions: safeHasRecentRevisions,
        loadingRecent,
        recentError,
        recentRevisions,
        selectedWeek,
        recentRevisionsCount: recentRevisions ? recentRevisions.length : 0
    });

    // Don't render if dismissed
    if (isDismissed) {
        return null;
    }

    // Show component even if no revisions for debugging
    // Later we can change this back to: if (!shouldShowSection) return null;

    // Handle dismiss
    const handleDismiss = () => {
        setIsDismissed(true);
    };

    // Handle toggle expand/collapse
    const handleToggleExpanded = () => {
        setIsExpanded(prev => !prev);
    };

    // Responsive carousel configuration
    const getItemsPerView = () => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth < 640) return 1; // mobile
            if (window.innerWidth < 768) return 2; // sm
            if (window.innerWidth < 1024) return 3; // md
            if (window.innerWidth < 1280) return 4; // lg
            return 5; // xl
        }
        return 5;
    };

    const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

    // Update items per view on window resize
    useEffect(() => {
        const handleResize = () => {
            setItemsPerView(getItemsPerView());
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const canScrollLeft = currentIndex > 0;
    const canScrollRight = currentIndex < Math.max(0, recentRevisions.length - itemsPerView);
    const displayRevisions = isExpanded ? recentRevisions : recentRevisions.slice(0, Math.min(3, recentRevisions.length));
    const hasMoreToShow = recentRevisions.length > 3;

    // Carousel navigation functions
    const scrollToNext = () => {
        if (canScrollRight) {
            setCurrentIndex(prev => Math.min(prev + 1, recentRevisions.length - itemsPerView));
        }
    };

    const scrollToPrev = () => {
        if (canScrollLeft) {
            setCurrentIndex(prev => Math.max(prev - 1, 0));
        }
    };

    // Reset carousel when revisions change
    useEffect(() => {
        setCurrentIndex(0);
    }, [recentRevisions.length]);

    return (
        <>
            {/* Main Section */}
            <div className="mb-8 animate-fade-in">
                <div className="bg-gradient-to-br from-orange-50 via-white to-white rounded-2xl border-2 border-orange-200 overflow-hidden shadow-md">
                    {/* Header */}
                    <div className="p-6 bg-gradient-to-r from-orange-100/30 to-transparent border-b border-orange-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-orange-100 rounded-lg">
                                    <Calendar size={24} className="text-orange-600" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-2xl font-bold text-slate-900">
                                            Global Revisions
                                        </h2>
                                        <span className="px-2.5 py-0.5 bg-orange-500 text-white text-xs font-semibold rounded-full">
                                            Latest
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm text-slate-600 mt-1.5">
                                        {selectedWeek && (
                                            <span className="flex items-center gap-1.5">
                                                📅 {selectedWeek.description}
                                            </span>
                                        )}
                                        <span className="font-medium">
                                            {recentStats.totalFound || recentRevisions.length} revision
                                            {(recentStats.totalFound || recentRevisions.length) !== 1 ? 's' : ''} found
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                {/* Refresh Button */}
                                <button
                                    onClick={refreshRecent}
                                    disabled={loadingRecent}
                                    className="p-2 text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                    title="Refresh"
                                >
                                    <RefreshCw size={16} className={loadingRecent ? 'animate-spin' : ''} />
                                </button>

                                {/* View All Button */}
                                <button
                                    onClick={openModal}
                                    className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all shadow-sm hover:shadow-md font-medium"
                                >
                                    <Eye size={16} />
                                    View All
                                </button>

                                {/* Expand/Collapse Button */}
                                <button
                                    onClick={handleToggleExpanded}
                                    className="p-2 text-slate-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </button>

                                {/* Dismiss Button */}
                                <button
                                    onClick={handleDismiss}
                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                    title="Dismiss"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    {isExpanded && (
                        <div className="p-6 bg-white/50">
                            {loadingRecent ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="text-center">
                                        <div className="relative">
                                            <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin mx-auto mb-3"></div>
                                        </div>
                                        <p className="text-slate-500 text-sm font-medium">Loading recent revisions...</p>
                                    </div>
                                </div>
                            ) : recentError ? (
                                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-red-900">Error Loading Revisions</h3>
                                        <p className="text-red-700 text-sm mt-1">{recentError}</p>
                                    </div>
                                </div>
                            ) : displayRevisions.length === 0 ? (
                                <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                                    <Info size={20} className="text-slate-500 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-slate-900">No Recent Revisions</h3>
                                        <p className="text-slate-600 text-sm mt-1">
                                            No global revisions found for the recent period.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {/* Horizontal Carousel */}
                                    {isExpanded ? (
                                        <div className="relative">
                                            {/* Carousel Navigation */}
                                            {recentRevisions.length > itemsPerView && (
                                                <>
                                                    <button
                                                        onClick={scrollToPrev}
                                                        disabled={!canScrollLeft}
                                                        className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white shadow-lg border border-slate-200 transition-all duration-200 ${canScrollLeft
                                                            ? 'hover:bg-orange-50 hover:border-orange-300 text-slate-700 hover:text-orange-600 hover:shadow-xl'
                                                            : 'text-slate-300 cursor-not-allowed opacity-50'
                                                            }`}
                                                        style={{ transform: 'translate(-12px, -50%)' }}
                                                    >
                                                        <ChevronLeft size={20} />
                                                    </button>
                                                    <button
                                                        onClick={scrollToNext}
                                                        disabled={!canScrollRight}
                                                        className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2.5 rounded-full bg-white shadow-lg border border-slate-200 transition-all duration-200 ${canScrollRight
                                                            ? 'hover:bg-orange-50 hover:border-orange-300 text-slate-700 hover:text-orange-600 hover:shadow-xl'
                                                            : 'text-slate-300 cursor-not-allowed opacity-50'
                                                            }`}
                                                        style={{ transform: 'translate(12px, -50%)' }}
                                                    >
                                                        <ChevronRight size={20} />
                                                    </button>
                                                </>
                                            )}

                                            {/* Cards Container */}
                                            <div className="overflow-hidden mx-4 carousel-container">
                                                <div
                                                    className="carousel-track gap-4"
                                                    style={{
                                                        transform: `translateX(-${(currentIndex * (100 / itemsPerView))}%)`,
                                                        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}
                                                >
                                                    {recentRevisions.map((revision, index) => (
                                                        <div
                                                            key={revision.id || index}
                                                            className="carousel-card"
                                                            style={{
                                                                width: `calc((100% - ${(itemsPerView - 1) * 16}px) / ${itemsPerView})`,
                                                                minWidth: '280px'
                                                            }}
                                                        >
                                                            <GlobalRevisionCard
                                                                revision={revision}
                                                                variant="compact"
                                                                showClient={true}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Carousel Indicators */}
                                            {recentRevisions.length > itemsPerView && (
                                                <div className="flex justify-center mt-4 gap-2">
                                                    {Array.from({ length: Math.ceil(recentRevisions.length / itemsPerView) }).map((_, index) => (
                                                        <button
                                                            key={index}
                                                            onClick={() => setCurrentIndex(index * itemsPerView)}
                                                            className={`w-2 h-2 rounded-full transition-colors ${Math.floor(currentIndex / itemsPerView) === index
                                                                ? 'bg-orange-600'
                                                                : 'bg-slate-300 hover:bg-slate-400'
                                                                }`}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        /* Collapsed view - show first 3 in a simple grid */
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            {displayRevisions.map((revision, index) => (
                                                <GlobalRevisionCard
                                                    key={revision.id || index}
                                                    revision={revision}
                                                    variant="compact"
                                                    showClient={true}
                                                />
                                            ))}
                                        </div>
                                    )}

                                    {/* Show More/View All Footer */}
                                    {hasMoreToShow && (
                                        <div className="flex items-center justify-between pt-4 mt-2 border-t border-orange-200/50">
                                            <div className="text-sm text-slate-600 font-medium">
                                                {isExpanded ?
                                                    `${recentRevisions.length} revision${recentRevisions.length !== 1 ? 's' : ''} available` :
                                                    `Showing 3 of ${recentRevisions.length} revisions`
                                                }
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {!isExpanded && (
                                                    <button
                                                        onClick={() => setIsExpanded(true)}
                                                        className="text-sm text-orange-600 hover:text-orange-700 font-semibold hover:underline transition-all"
                                                    >
                                                        Show all {recentRevisions.length} →
                                                    </button>
                                                )}
                                                <button
                                                    onClick={openModal}
                                                    className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700 font-semibold hover:underline transition-all"
                                                >
                                                    View all revisions →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Collapsed State */}
                    {!isExpanded && (
                        <div className="px-6 py-4 bg-white/50">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-slate-600 font-medium">
                                    {recentRevisions.length} revision{recentRevisions.length !== 1 ? 's' : ''} available
                                </span>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setIsExpanded(true)}
                                        className="text-orange-600 hover:text-orange-700 font-semibold hover:underline transition-all"
                                    >
                                        Show all
                                    </button>
                                    <span className="text-slate-300">•</span>
                                    <button
                                        onClick={openModal}
                                        className="flex items-center gap-1.5 text-orange-600 hover:text-orange-700 font-semibold hover:underline transition-all"
                                    >
                                        View all
                                        <ChevronRight size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <GlobalRevisionsModal
                isOpen={isModalOpen}
                onClose={closeModal}
                revisions={allRevisions}
                loading={loadingAll}
                error={allError}
                hasMore={hasMoreRevisions}
                onLoadMore={loadMoreRevisions}
                onSearch={searchRevisions}
                onFilterChange={updateFilters}
                filters={filters}
                selectedWeek={selectedWeek}
                stats={allStats}
                testMode={testMode}
            />
        </>
    );
}
